import { useState, useEffect, useCallback } from 'react';
import { Transaction, TransactionInput } from '../types';
import { api } from '../services/api';

function parseTxn(t: Transaction): Transaction {
  return {
    ...t,
    date: t.date.split('T')[0],
    payment: t.payment !== null ? Number(t.payment) : null,
    deposit: t.deposit !== null ? Number(t.deposit) : null,
  };
}

export function useTransactions(accountId: number | null) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTransactions = useCallback(async () => {
    if (!accountId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await api.getTransactions(accountId);
      setTransactions(data.map(parseTxn));
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const addTransaction = async (input: TransactionInput) => {
    if (!accountId) return;
    const data = await api.createTransaction(accountId, input);
    const parsed = parseTxn(data);
    setTransactions(prev =>
      [...prev, parsed].sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
    );
    return parsed;
  };

  const updateTransaction = async (id: number, input: TransactionInput) => {
    if (!accountId) return;
    const data = await api.updateTransaction(accountId, id, input);
    const parsed = parseTxn(data);
    setTransactions(prev =>
      prev.map(t => (t.id === id ? parsed : t)).sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
    );
    return parsed;
  };

  const toggleCleared = async (id: number) => {
    if (!accountId) return;
    const data = await api.toggleCleared(accountId, id);
    const parsed = parseTxn(data);
    setTransactions(prev => prev.map(t => (t.id === id ? parsed : t)));
  };

  const deleteTransaction = async (id: number) => {
    if (!accountId) return;
    await api.deleteTransaction(accountId, id);
    setTransactions(prev => prev.filter(t => t.id !== id));
  };

  const importTransactions = (imported: Transaction[]) => {
    const parsed = imported.map(parseTxn);
    setTransactions(prev =>
      [...prev, ...parsed].sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
    );
  };

  return { transactions, loading, addTransaction, updateTransaction, toggleCleared, deleteTransaction, importTransactions };
}
